import { useMemo } from "react";
import { Pencil, Plus, Trash2, UserCheck, Users } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { OperadorCatalogDto } from "@/services/locacoes";
import {
  locacoesSectionTitleClass,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
} from "./locacoesUtils";
import { LocacoesStatCard } from "./LocacoesStatCard";
import { LocacoesSubToolbar } from "./LocacoesSubToolbar";

interface Props {
  operadores: OperadorCatalogDto[];
  busy?: boolean;
  onNovoOperador: () => void;
  onEditOperador: (o: OperadorCatalogDto) => void;
  onDeleteOperador: (o: OperadorCatalogDto) => void;
}

export function TabCadastroOperadores({
  operadores,
  busy,
  onNovoOperador,
  onEditOperador,
  onDeleteOperador,
}: Props) {
  const kpi = useMemo(
    () => ({
      total: operadores.length,
      comLogin: operadores.filter((o) => Boolean(o.usuarioId)).length,
      semLogin: operadores.filter((o) => !o.usuarioId).length,
    }),
    [operadores],
  );

  return (
    <div className={locacoesTabContentClass}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <LocacoesStatCard title="Total" value={kpi.total} icon={<Users className="h-5 w-5" />} accentClass="border-l-4 border-l-blue-400" />
        <LocacoesStatCard title="Com login" value={kpi.comLogin} icon={<UserCheck className="h-5 w-5" />} accentClass="border-l-4 border-l-emerald-400" />
        <LocacoesStatCard title="Sem login" value={kpi.semLogin} icon={<Users className="h-5 w-5" />} accentClass="border-l-4 border-l-purple-400" />
      </div>

      <LocacoesSubToolbar
        title="Cadastro"
        end={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={onNovoOperador}>
            Novo operador
          </Button>
        }
      >
        <div className={locacoesSectionTitleClass}>
          <Users className="h-4 w-4 text-brand-600" />
          <span>Operadores cadastrados ({operadores.length})</span>
        </div>
        <p className="mt-2 text-sm text-slate-500">Disponíveis para vinculação em contratos e registros de horímetro.</p>
      </LocacoesSubToolbar>

      <div className={locacoesTableWrapClass}>
        <table className="min-w-full text-sm">
          <thead className={locacoesTableHeadClass}>
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Documento</th>
              <th className="px-4 py-3">Telefone</th>
              <th className="px-4 py-3">Observação</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {operadores.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  Nenhum operador cadastrado.
                </td>
              </tr>
            ) : (
              operadores.map((op) => (
                <tr key={op.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-medium text-slate-800">{op.nome}</td>
                  <td className="px-4 py-3 text-slate-600">{op.documento || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{op.telefone || "—"}</td>
                  <td className="max-w-xs truncate px-4 py-3 text-slate-500">{op.observacao || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditOperador(op)}
                        leftIcon={<Pencil className="h-3.5 w-3.5" />}
                        disabled={busy}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteOperador(op)}
                        leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                        disabled={busy}
                      >
                        Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
